import React, {useEffect, useState} from 'react';
import {BrowserRouter as Router} from 'react-router-dom';
import Navbar from './components/Navbar';
import ErrorBoundary from "./components/ErrorBoundary";

import {useAppDispatch, useAppSelector} from './stores/store';
import {validateToken} from './features/auth/authSlice';
import {SnackbarProvider, useSnackbar} from "notistack";
import AgeGate from "./components/AgeGate";
import AppRouters from "./AppRouters";
import {clearNotifications} from "./features/notification/notificationSlice";
import {setSnackbar} from "./utils/notificationManager";
import AppModals from "./AppModals";
import useGlobalStyles from './styles/global';
import Footer from "./components/Footer";
import ScrollToTop from "./components/ScrollToTop";
import LoadingComponent from "./components/kozel/LoadingComponent";
import RouteTracking from "./RouteTracking";

const SnackbarInitializer = ({children}: { children: React.ReactNode }) => {
    const {enqueueSnackbar, closeSnackbar} = useSnackbar();
    setSnackbar(enqueueSnackbar, closeSnackbar);
    return <>{children}</>;
};

const App: React.FC = () => {
    useGlobalStyles();

    const dispatch = useAppDispatch();
    const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
    const [keepItAge, setKeepItAge] = useState(
        Boolean(localStorage.getItem('keepItAge')) || false
    );

    const [authLoading, setAuthLoading] = useState(true);
    const storageSave = keepItAge ? localStorage : sessionStorage;
    const [age, setAge] = useState(parseInt(storageSave.getItem('age') || '0'));
    const [expireSetAge, setExpireSetAge] = useState(storageSave.getItem('expire_set_age'));
    const handleAgeChecker = (age: number, keepIt: boolean) => {
        if (keepIt) {
            setKeepItAge(true);
            localStorage.setItem('keepItAge', Boolean(keepIt).toString());
        }
        setAge(age);
        storageSave.setItem('age', age.toString());

        const expirationDate = new Date();
        expirationDate.setSeconds(expirationDate.getSeconds() + parseInt(process.env.REACT_APP_AGEGATE_EXPIRATION_SECONDS || '3600'));
        storageSave.setItem('expire_set_age', expirationDate.toISOString());
        setExpireSetAge(expirationDate.toISOString());
    };

    useEffect(() => {
        const interval = setInterval(() => {
            const currentDate = new Date();
            const expirationDate = new Date(expireSetAge || '');

            if (currentDate > expirationDate) {
                setAge(0);
                storageSave.removeItem('age');
                storageSave.removeItem('expire_set_age');
                clearInterval(interval);
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [expireSetAge,storageSave]);

    useEffect(() => {
        if (age >= 18) {
            dispatch(validateToken()).finally(() => {
                setAuthLoading(false);
            });
        } else {
            setAuthLoading(false);
        }
    }, [age, dispatch]);

    if (authLoading) return <LoadingComponent/>;

    return (
        <>
            <SnackbarProvider maxSnack={3} onClose={e => {
                dispatch(clearNotifications())
            }}>
                <SnackbarInitializer>
                    {age < 18 ?
                        <AgeGate onAgeSubmit={handleAgeChecker}/> :
                        <Router>
                            <ScrollToTop/>
                            <RouteTracking />
                            <ErrorBoundary>
                                <Navbar/>
                                <AppModals/>
                                <AppRouters isAuthenticated={isAuthenticated}/>
                                <Footer/>
                            </ErrorBoundary>
                        </Router>
                    }
                </SnackbarInitializer>
            </SnackbarProvider>
        </>
    );
};

export default App;
